import { useState, useEffect, useRef, useSyncExternalStore } from "react";
import { getQueueSize, getFailedMutations, retryFailed, removeMutation, flushQueue, type QueuedMutation } from "../../lib/api/offline-queue";
import { apiFetch } from "../../lib/api/client";
import { getConnectivityStatus, subscribe as connectivitySubscribe, onSyncComplete, emitSyncComplete, type SyncResult } from "../../lib/connectivity";

const ENTITY_LABELS: Record<string, string> = {
  task: "Tâche",
  event: "Événement",
  habit: "Habitude",
  category: "Catégorie",
  subtask: "Sous-tâche",
};

const TYPE_LABELS: Record<string, string> = {
  create: "Création",
  update: "Modification",
  delete: "Suppression",
};

type Status = "online" | "client-offline" | "server-unreachable";

function useConnectivity(): Status {
  const serverStatus = useSyncExternalStore(connectivitySubscribe, getConnectivityStatus);
  const browserOnline = useSyncExternalStore(
    (cb) => {
      window.addEventListener("online", cb);
      window.addEventListener("offline", cb);
      return () => { window.removeEventListener("online", cb); window.removeEventListener("offline", cb); };
    },
    () => navigator.onLine,
  );
  if (!browserOnline) return "client-offline";
  return serverStatus;
}

const OFFLINE_MESSAGES: Record<string, string> = {
  "client-offline": "Hors-ligne — vos modifications sont sauvegardées localement",
  "server-unreachable": "Serveur indisponible — vos modifications sont sauvegardées localement",
};

function BannerShell({ children }: { readonly children: React.ReactNode }) {
  return (
    <div className="fixed bottom-20 md:bottom-6 left-1/2 -translate-x-1/2 z-100 animate-slide-up w-[calc(100%-2rem)] max-w-sm md:w-auto">
      <div className="bg-surface border border-border rounded-xl shadow-xl px-4 py-3">
        {children}
      </div>
    </div>
  );
}

export function OfflineBanner() {
  const status = useConnectivity();
  const isOffline = status !== "online";
  const [showReconnected, setShowReconnected] = useState(false);
  const [syncSuccess, setSyncSuccess] = useState<SyncResult | null>(null);
  const wasOfflineRef = useRef(false);
  const [queueSize, setQueueSize] = useState(0);
  const [failedMutations, setFailedMutations] = useState<QueuedMutation[]>([]);
  const [showDétails, setShowDétails] = useState(false);

  useEffect(() => {
    if (isOffline) {
      wasOfflineRef.current = true;
      return;
    }
    if (wasOfflineRef.current) {
      wasOfflineRef.current = false;
      const t = setTimeout(() => setShowReconnected(true), 0);
      const hide = setTimeout(() => setShowReconnected(false), 3000);
      return () => { clearTimeout(t); clearTimeout(hide); };
    }
  }, [isOffline]);

  useEffect(() => {
    const refresh = () => {
      const size = getQueueSize();
      const failed = getFailedMutations();
      setQueueSize((prev) => prev === size ? prev : size);
      setFailedMutations((prev) => prev.length === failed.length ? prev : failed);
    };
    // Poll plus souvent quand offline ou queue non vide, sinon rarement
    const interval = isOffline || queueSize > 0 || failedMutations.length > 0 ? 2000 : 30000;
    const poll = setInterval(refresh, interval);
    const init = setTimeout(refresh, 0);
    return () => { clearInterval(poll); clearTimeout(init); };
  }, [isOffline, queueSize, failedMutations.length]);

  useEffect(() => {
    return onSyncComplete((result) => {
      if (result.succeeded > 0) {
        const t = setTimeout(() => setSyncSuccess(result), 0);
        const hide = setTimeout(() => setSyncSuccess(null), 4000);
        return () => { clearTimeout(t); clearTimeout(hide); };
      }
    });
  }, []);

  const handleRetryAll = () => {
    retryFailed();
    setFailedMutations([]);
    flushQueue(apiFetch).then((result) => {
      emitSyncComplete(result);
    });
  };

  const handleRemove = (id: string) => {
    removeMutation(id);
    setFailedMutations((prev) => prev.filter((m) => m.id !== id));
    setQueueSize((prev) => prev - 1);
  };

  if (isOffline) {
    return (
      <BannerShell>
        <div className="flex items-center gap-3">
          <span className="shrink-0 w-2 h-2 rounded-full bg-yellow-500 animate-pulse" />
          <div className="flex flex-col gap-0.5">
            <p className="text-sm text-ink font-medium">{OFFLINE_MESSAGES[status]}</p>
            {queueSize > 0 && (
              <p className="text-xs text-ink-muted">{queueSize} modification{queueSize > 1 ? "s" : ""} en attente</p>
            )}
          </div>
        </div>
      </BannerShell>
    );
  }

  if (showReconnected) {
    return (
      <BannerShell>
        <div className="flex items-center gap-3">
          <span className="shrink-0 w-2 h-2 rounded-full bg-green-500" />
          <p className="text-sm text-ink font-medium">Connexion rétablie</p>
        </div>
      </BannerShell>
    );
  }

  if (syncSuccess) {
    return (
      <BannerShell>
        <div className="flex items-center gap-3">
          <span className="shrink-0 w-2 h-2 rounded-full bg-green-500" />
          <div className="flex flex-col gap-0.5">
            <p className="text-sm text-ink font-medium">
              {syncSuccess.succeeded} modification{syncSuccess.succeeded > 1 ? "s" : ""} synchronisée{syncSuccess.succeeded > 1 ? "s" : ""}
            </p>
            {syncSuccess.rejected > 0 && (
              <p className="text-xs text-ink-muted">{syncSuccess.rejected} échouée{syncSuccess.rejected > 1 ? "s" : ""}</p>
            )}
          </div>
        </div>
      </BannerShell>
    );
  }

  if (failedMutations.length > 0) {
    return (
      <BannerShell>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="shrink-0 w-2 h-2 rounded-full bg-red-500" />
            <p className="text-sm text-ink font-medium">
              {failedMutations.length} modification{failedMutations.length > 1 ? "s" : ""} non synchronisée{failedMutations.length > 1 ? "s" : ""}
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <button onClick={() => setShowDétails(!showDétails)} className="text-xs text-ink-muted hover:text-ink transition-colors">
              {showDétails ? "Masquer" : "Détails"}
            </button>
            <button onClick={handleRetryAll} className="px-3 py-1 text-xs font-semibold bg-accent text-white rounded-lg hover:opacity-80 transition-opacity">
              Réessayer
            </button>
          </div>
        </div>

        {showDétails && (
          <div className="mt-2 border-t border-border pt-2 space-y-1.5 max-h-40 overflow-y-auto">
            {failedMutations.map((m) => (
              <div key={m.id} className="flex items-center justify-between text-xs text-ink-muted">
                <span>{TYPE_LABELS[m.type] ?? m.type} — {ENTITY_LABELS[m.entity] ?? m.entity}</span>
                <button onClick={() => handleRemove(m.id)} className="text-ink-muted hover:text-red-500 transition-colors ml-2" title="Supprimer">
                  x
                </button>
              </div>
            ))}
          </div>
        )}
      </BannerShell>
    );
  }

  if (queueSize > 0) {
    return (
      <BannerShell>
        <div className="flex items-center gap-3">
          <span className="shrink-0 w-2 h-2 rounded-full bg-accent animate-pulse" />
          <p className="text-sm text-ink font-medium">
            Synchronisation de {queueSize} modification{queueSize > 1 ? "s" : ""}...
          </p>
        </div>
      </BannerShell>
    );
  }

  return null;
}
